<template>
  <Overlay
    ref="componentOverlayRef"
    class="overlay__container"
    @close-overlay="setPosition(undefined)"
  >
    <template #title>
      <div
        class="overlay__title-container"
      >
        <span
          class="overlay__title--title"
        >
          {{ elementTitle }}
        </span>
        <span
          v-show="!!layerDescription"
          class="overlay__title--subtitle"
        >
          {{ layerDescription }}
        </span>
      </div>
    </template>
    <template
      v-if="!!popupIcon?.icon"
      #icon
    >
      <span
        v-if="popupIcon.type === 'material-icon'"
        class="material-icons"
      >{{ popupIcon.icon }}</span>
    </template>
    <template #default>
      <div class="overlay__field-list flex flex-column">
        <div
          v-for="(field, index) in textFields"
          :key="index"
          class="overlay__field-info"
        >
          <span class="overlay__field-description">{{ field.description }}: </span>
          <span class="overlay__field-value">{{ field.value }}</span>
        </div>
        <div
          v-for="(field, index) in booleanFields"
          :key="index"
          class="overlay__field-info"
        >
          <span class="overlay__field-description">{{ field.description }}: </span>
          <span class="material-icons overlay__field-boolean">
            flag
          </span>
        </div>
        <div
          v-for="(field, index) in imageFields"
          :key="index"
          class="overlay__field-info overlay__field-image-container"
        >
          <img
            :src="field.url"
            :alt="field.imageName"
            class="overlay__field-image"
          />
        </div>
      </div>
    </template>
    <template #footer>
      <div
        v-if="!!relatedElements?.length"
        class="overlay__related-elements-paginator"
      >
        <div
          v-if="!currentRelatedElementIndex"
        ></div>
        <Button
          v-if="currentRelatedElementIndex > 0"
          v-tooltip.right="'Anterior elemento'"
          class="overlay__related-elements-button overlay__related-elements-button--left"
          icon="pi pi-arrow-left"
          @click="goPreviousRelatedElement"
        ></Button>
        <span class="overlay__related-elements-paginator--info">{{ paginatorInfo }}</span>
        <Button
          v-if="currentRelatedElementIndex < relatedElements.length"
          v-tooltip.left="'Siguiente elemento'"
          class="overlay__related-elements-button overlay__related-elements-button--right"
          icon="pi pi-arrow-right"
          @click="goNextRelatedElement"
        ></Button>
        <div
          v-if="currentRelatedElementIndex == relatedElements.length"
        ></div>
      </div>
    </template>
  </Overlay>
</template>

<script setup>
import {ref,
  inject,
  computed,
  watch,
} from 'vue';
import Button from 'primevue/button';
import Overlay from '@/components/overlay-map/Overlay';

// #region Props, Emits
// ...
// #endregion


// #region Params, Ref, Reactives, Computed
const instanceMapRef = inject('instanceMapRef');
const maplibreRef = inject('maplibreRef');
const infoOverlayRef = inject('infoOverlayRef');
const componentOverlayRef = ref();
const currentRelatedElementIndex = ref(null);
const savedIconOpacity = ref(null);
const savedLayerName = ref(null);

const popupIcon = computed(() => infoOverlayRef?.value?.icon);
const relatedElementsField = computed(() =>
  infoOverlayRef?.value?.fields?.filter((f) => f?.fieldType === 'connectedElements' && !!f?.value));
const relatedElements = computed(() => {
  if (!!relatedElementsField?.value?.length) {
    return relatedElementsField.value[0].relatedOverlayInfoList;
  }
  return [];
});
const currentElement = computed(() => {
  if (!!relatedElements?.value?.length &&
     currentRelatedElementIndex?.value > 0) {
    return relatedElements.value[currentRelatedElementIndex.value - 1];
  }
  return infoOverlayRef.value;
});
const layerDescription = computed(() => currentElement?.value?.layerDescription);
const booleanFields = computed(() =>
  currentElement?.value?.fields?.filter((f) => f?.fieldType === 'boolean' && !!f?.value));

const imageFields = computed(() =>
  currentElement?.value?.fields?.filter((f) => f?.fieldType === 'imageUrl'));
const textFields = computed(() =>
  currentElement?.value?.fields?.filter((f) => (f?.fieldType !== 'imageUrl') &&
                                               (f?.fieldType !== 'boolean') &&
                                               (f?.fieldType !== 'connectedElements'),
  ));
const elementTitle = computed(() => textFields?.value?.find((el) => !!el?.isTitle)?.value);
const paginatorInfo = computed(() => {
  if (!!relatedElements?.value?.length) {
    const index = currentRelatedElementIndex?.value == null ? 1 :
      currentRelatedElementIndex.value + 1;
    return index + ' de ' + (relatedElements.value.length + 1);
  }
  return '';
});
// #endregion

// #region Functions
function resetIconOpacity() {
  if (savedIconOpacity?.value && savedLayerName?.value) {
    maplibreRef.value.setPaintProperty(savedLayerName.value,
      'icon-opacity', savedIconOpacity.value);
  }
  savedIconOpacity.value = null;
  savedLayerName.value = null;
}
function setPosition(coordinate, preserveRelatedIndex = false) {
  if (!preserveRelatedIndex) {
    currentRelatedElementIndex.value = null;
    resetIconOpacity();
  }
  componentOverlayRef.value.overlay.setPosition(coordinate);
}
function goPreviousRelatedElement() {
  if (currentRelatedElementIndex.value > 0) {
    currentRelatedElementIndex.value = currentRelatedElementIndex.value - 1;
  }
}
function goNextRelatedElement() {
  if (currentRelatedElementIndex.value < relatedElements.value.length) {
    currentRelatedElementIndex.value = currentRelatedElementIndex.value + 1;
  }
}
// #endregion

// #region Effects, Hooks
watch([currentRelatedElementIndex, currentElement], ([newIndex, newCurrentElement]) => {
  if (!!newCurrentElement?.coord3857?.length && newIndex != null) {
    if (newCurrentElement?.id && newCurrentElement?.layerName) {
      if ((newCurrentElement.layerName !== savedLayerName.value) ||
         !savedIconOpacity?.value
      ) {
        savedLayerName.value = newCurrentElement.layerName;
        savedIconOpacity.value =
          maplibreRef.value.getPaintProperty(newCurrentElement.layerName, 'icon-opacity');
      }
      const newIconOpacity = ['case',
        ['==', ['get', 'id'], newCurrentElement.id], 1,
        ['==', ['get', 'visibility'], null], 1,
        ['get', 'visibility'],
      ];
      maplibreRef.value.setPaintProperty(newCurrentElement.layerName,
        'icon-opacity', newIconOpacity);
    }
    setPosition(newCurrentElement.coord3857, true);
  }
});
const unwatch = watch([instanceMapRef, componentOverlayRef], () => {});
watch([instanceMapRef, componentOverlayRef],
  ([newInstanceMapRef, newComponentOverlayRef]) => {
    if (!!newInstanceMapRef?.addOverlay &&
        !!newComponentOverlayRef?.overlay
    ) {
      setTimeout(() => {
        instanceMapRef.value.addOverlay(newComponentOverlayRef.overlay);
      }, 1000);
      // overlayLoaded.value = true;
      unwatch();
    }
  });
// #endregion

// #region Provide, Expose
defineExpose({
  setPosition,
});
// #endregion
</script>

<style lang="scss" scoped>
.overlay {
  &__container {
    &__loading {
      width: 100px;
    }
    margin: 0.5rem;
  }
  &__title {
    &-container {
      margin: 0 0.3rem;
      display: flex;
      flex-direction: column;
    }
    &--subtitle {
      margin-top: 0.3em;
      font-size: 0.8em;
    }
  }
  &__field {
    &-info {
      display: flex;
      flex-flow: row wrap;
      align-items: center;
      justify-content: flex-start;
    }
    &-boolean {
      color: $brand__color--blue;
    }
    &-image {
      max-height: 40dvh;
      &-container {
        margin: 0 auto;
      }
    }
    &-description {
      padding-right: 5px;
      font-weight: bold;
    }
  }
  &__related-elements {
    &-paginator {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }
  }
}

.overlay__content {
  font-size: 0.9rem;

  &__group {
    margin-top: 8px;
    font-size: 0.9em;
    display: flex;
    position: relative;
    width: fit-content;

    &__helper {
      position: absolute;
      left: 100%;
      top: 100%;
      padding: 5px;
      width: 300px;
      background-color: rgba($brand__color--white, 0.8);
      box-shadow: 0 1px 4px rgba($brand__color--black,0.2);
      border-radius: 0.25rem;
      border: 1px solid $brand__color--smoke;
      display: none;
      z-index: 9999;

      &--open {
        display: block;
      }
    }

    &-button {
      margin-top: 10px;
      display: flex;
      width: 100%;

      &__button {
        width: 100%;
      }
    }
  }

  &__loading {
    display: flex;
    justify-content: center;
    align-items: center;

    & > span {
      font-size: 3rem;
      animation-name: loader;
      animation-duration: 2s;
      animation-iteration-count: infinite;
      color: $brand__color--blue;
    }
  }
}
</style>
